import React from 'react'
import { geography, ownerShipType } from '../LocalData/Constants'
import { useTheme } from "../context/ThemeContext";
import { runSharedKpiCalculations } from '../utils/kpiCalculationSync';
// import { runKpiCalculations } from '../utils/kpiApi'

const initialFormData = {
  companyRevenue: '',
  ebitda: '',
  numberOfEmployees: '',
  ltmDate: '',
  censusDate: '',
  geography: '',
  ownershipType: '',
  projectName: '',
  dataType: '',
  date: '',
  accountAligned: '',
  industry: '',
  sector: '',
  subsector: '',
  freeFormComments: '',
}

type FormDataState = typeof initialFormData

const fieldLabels = {
  companyRevenue: 'Company Revenue',
  ebitda: 'EBITDA',
  numberOfEmployees: 'Number of Employees',
  ltmDate: 'LTM Date',
  censusDate: 'Census Date',
  geography: 'Geography',
  ownershipType: 'Ownership Type',
  projectName: 'Project Name',
  accountAligned: 'Account Aligned',
  industry: 'Industry',
  sector: 'Sector',
  subsector: 'Subsector',
}

const TEMPLATE_FILE_NAME =
  "Deal Data Input Guidance Template (1).xlsx";
const TEMPLATE_FILE_PATH =
  `/${encodeURIComponent(TEMPLATE_FILE_NAME)}`;
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')

const VALID_XLSX_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/octet-stream',
])

const METADATA_FIELD_ALIASES = {
  companyrevenue: 'companyRevenue',
  ebitda: 'ebitda',
  numberofemployees: 'numberOfEmployees',
  industry: 'industry',
  sector: 'sector',
  subsector: 'subsector',
  geography: 'geography',
  ownershiptype: 'ownershipType',
  projectname: 'projectName',
  projectnametarget: 'projectName',
  accountaligned: 'accountAligned',
  accountalignedpefirm: 'accountAligned',
  censusdate: 'censusDate',
  ltmdate: 'ltmDate',
} as const

type MetadataAliasKey = keyof typeof METADATA_FIELD_ALIASES

type TemplateValidationField = {
  is_valid: boolean
  value?: string
}

type TemplateValidationResult = {
  is_template_match: boolean
  industry?: TemplateValidationField
  sector?: TemplateValidationField
  subsector?: TemplateValidationField
}

const normalizeMetadataKey = (key) =>
  String(key || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const isValidXlsxFile = (file) => {
  if (!file) return false

  const fileName = String(file.name || '').toLowerCase()
  const hasXlsxExtension = fileName.endsWith('.xlsx')
  const hasValidMime = file.type ? VALID_XLSX_MIME_TYPES.has(file.type) : false

  return hasXlsxExtension || hasValidMime
}

const pad2 = (num) => String(num).padStart(2, '0')

const toYyyyMmDd = (year, month, day) => {
  const y = Number(year)
  const m = Number(month)
  const d = Number(day)

  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return ''
  if (m < 1 || m > 12 || d < 1 || d > 31) return ''

  return `${y}-${pad2(m)}-${pad2(d)}`
}

const formatDateForInput = (value) => {
  if (value == null || value === '') return ''

  // Handle Excel date serial values (days since 1899-12-30)
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value.trim()))) {
    const serial = Number(value)

    if (serial > 59 && serial < 100000) {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30))
      const converted = new Date(excelEpoch.getTime() + Math.floor(serial) * 24 * 60 * 60 * 1000)

      if (!Number.isNaN(converted.getTime())) {
        return toYyyyMmDd(converted.getUTCFullYear(), converted.getUTCMonth() + 1, converted.getUTCDate())
      }
    }
  }

  const raw = String(value).trim()

  // YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) {
    return toYyyyMmDd(isoMatch[1], isoMatch[2], isoMatch[3])
  }

  // DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY, MM-DD-YYYY
  const slashDashMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
  if (slashDashMatch) {
    const first = Number(slashDashMatch[1])
    const second = Number(slashDashMatch[2])
    const year = Number(slashDashMatch[3])

    // Heuristic: if first part > 12, treat as DD/MM/YYYY else treat as MM/DD/YYYY
    if (first > 12) {
      return toYyyyMmDd(year, second, first)
    }

    return toYyyyMmDd(year, first, second)
  }

  const parsedDate = new Date(raw)
  if (Number.isNaN(parsedDate.getTime())) return ''

  return toYyyyMmDd(parsedDate.getFullYear(), parsedDate.getMonth() + 1, parsedDate.getDate())
}

const formatNumberToTwoDecimals = (value) => {
  if (value == null || value === '') return ''

  const num = Number(value)
  if (Number.isNaN(num)) return ''

  return num.toFixed(2)
}

const buildMetadataPayload = (formData) => ({
  project_name: String(formData.projectName || '').trim(),
  company_revenue: String(formData.companyRevenue || '').trim(),
  ebitda: String(formData.ebitda || '').trim(),
  number_of_employees: String(formData.numberOfEmployees || '').trim(),
  industry: String(formData.industry || '').trim(),
  sector: String(formData.sector || '').trim(),
  subsector: String(formData.subsector || '').trim(),
  census_date: String(formData.censusDate || '').trim(),
  ltm_date: String(formData.ltmDate || '').trim(),
  geography: String(formData.geography || '').trim(),
  ownership_type: String(formData.ownershipType || '').trim(),
  account_aligned: String(formData.accountAligned || '').trim(),
})

const mapMetadataToFormData = (metadata): FormDataState =>
  Object.entries(metadata || {}).reduce((acc, [apiField, rawValue]) => {
    const normalizedKey = normalizeMetadataKey(apiField)
    const formField = METADATA_FIELD_ALIASES[normalizedKey as MetadataAliasKey]

    if (!formField) return acc

    if (formField === 'ltmDate' || formField === 'censusDate') {
      acc[formField] = formatDateForInput(rawValue)
      return acc
    }

    if (formField === 'ebitda' || formField === 'companyRevenue') {
      acc[formField] = formatNumberToTwoDecimals(rawValue)
      return acc
    }

    acc[formField] = rawValue == null ? '' : String(rawValue)
    return acc
  }, { ...initialFormData })

const getMissingMetadataFields = (mappedFormData) =>
  Object.entries(fieldLabels).reduce((missingFields, [fieldName, label]) => {
    if (String(mappedFormData[fieldName] || '').trim() === '') {
      missingFields.push(label)
    }

    return missingFields
  }, [])

const getRemainingMissingFields = (initialMissingFields, currentFormData) =>
  initialMissingFields.filter((label) => {
    const fieldName = Object.entries(fieldLabels).find(([, fieldLabel]) => fieldLabel === label)?.[0]
    if (!fieldName) return false
    return String(currentFormData[fieldName] || '').trim() === ''
  })

const getDropdownItems = async (url, signal) => {
  const response = await fetch(url, { signal })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || 'Failed to fetch dropdown options')
  }

  const data = await response.json()
  return Array.isArray(data?.items) ? data.items : []
}

const validateTemplateValues = async (
  industry,
  sector,
  subsector,
): Promise<TemplateValidationResult> => {
  if (!industry && !sector && !subsector) {
    return { is_template_match: true }
  }

  try {
    const params = new URLSearchParams()
    if (industry) params.append('industry', industry)
    if (sector) params.append('sector', sector)
    if (subsector) params.append('subsector', subsector)

    const response = await fetch(
      `${API_BASE_URL}/api/company-metadata/dropdowns/validate-template?${params}`,
    )

    if (!response.ok) {
      console.error('Validation response not ok')
      return { is_template_match: false }
    }

    return (await response.json()) as TemplateValidationResult
  } catch (error) {
    console.error('Template validation failed', error)
    return { is_template_match: false }
  }
}


const ContributeDataPage = () => {
  const { isDarkMode } = useTheme();
  const [selectedFile, setSelectedFile] = React.useState(null)
  const [isDragActive, setIsDragActive] = React.useState(false)
  const [formData, setFormData] = React.useState(initialFormData)
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false)
  const [missingMetadataFields, setMissingMetadataFields] = React.useState([])
  const [fileTypeError, setFileTypeError] = React.useState('')
  const [industryOptions, setIndustryOptions] = React.useState([])
  const [sectorOptions, setSectorOptions] = React.useState([])
  const [subsectorOptions, setSubsectorOptions] = React.useState([])
  const [dropdownError, setDropdownError] = React.useState('')
  const [prefillValidationErrors, setPrefillValidationErrors] =
    React.useState<TemplateValidationResult | null>(null)
  const [showSubmitSuccessPopup, setShowSubmitSuccessPopup] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isFormDataReady, setIsFormDataReady] = React.useState(false)
  const fileInputRef = React.useRef(null)

  React.useEffect(() => {
    const controller = new AbortController()

    const loadIndustries = async () => {
      try {
        setDropdownError('')
        const items = await getDropdownItems(
          `${API_BASE_URL}/api/company-metadata/dropdowns/industries`,
          controller.signal,
        )
        setIndustryOptions(items)
      } catch (error) {
        if (error.name === 'AbortError') return
        console.error('Failed to load industries', error)
        setIndustryOptions([])
        setDropdownError('Unable to load dropdown options. Please try again.')
      }
    }

    loadIndustries()

    return () => controller.abort()
  }, [])

  React.useEffect(() => {
    if (!formData.industry) {
      setSectorOptions([])
      setSubsectorOptions([])
      return
    }

    const controller = new AbortController()

    const loadSectors = async () => {
      try {
        setDropdownError('')
        const queryIndustry = encodeURIComponent(formData.industry)
        const items = await getDropdownItems(
          `${API_BASE_URL}/api/company-metadata/dropdowns/sectors?industry=${queryIndustry}`,
          controller.signal,
        )
        setSectorOptions(items)
      } catch (error) {
        if (error.name === 'AbortError') return
        console.error('Failed to load sectors', error)
        setSectorOptions([])
        setDropdownError('Unable to load dropdown options. Please try again.')
      }
    }

    loadSectors()

    return () => controller.abort()
  }, [formData.industry])

  React.useEffect(() => {
    if (!formData.industry || !formData.sector) {
      setSubsectorOptions([])
      return
    }

    const controller = new AbortController()

    const loadSubsectors = async () => {
      try {
        setDropdownError('')
        const queryIndustry = encodeURIComponent(formData.industry)
        const querySector = encodeURIComponent(formData.sector)

        try {
          const items = await getDropdownItems(
            `${API_BASE_URL}/api/company-metadata/dropdowns/subsectors?industry=${queryIndustry}&sector=${querySector}`,
            controller.signal,
          )
          setSubsectorOptions(items)
          return
        } catch (primaryError) {
          if (primaryError.name === 'AbortError') return

          const fallbackItems = await getDropdownItems(
            `${API_BASE_URL}/api/company-metadata/dropdowns/sectors?industry=${queryIndustry}&sector=${querySector}`,
            controller.signal,
          )
          setSubsectorOptions(fallbackItems)
        }
      } catch (error) {
        if (error.name === 'AbortError') return
        console.error('Failed to load subsectors', error)
        setSubsectorOptions([])
        setDropdownError('Unable to load dropdown options. Please try again.')
      }
    }

    loadSubsectors()

    return () => controller.abort()
  }, [formData.industry, formData.sector])

  const handleInputChange = (event) => {
    const { name, value } = event.target

    setFormData((previous) => {
      if (name === 'industry') {
        return { ...previous, industry: value, sector: '', subsector: '' }
      }

      if (name === 'sector') {
        return { ...previous, sector: value, subsector: '' }
      }

      return { ...previous, [name]: value }
    })
  }

  const handleFileChange = (event) => {
    if (selectedFile) return

    const file = event.target.files?.[0] || null

    if (!file) return

    if (!isValidXlsxFile(file)) {
      setFileTypeError('Only .xlsx files are allowed.')

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      return
    }

    setFileTypeError('')

    previewUploadedFile(file)
  }

  const handleDrop = (event) => {
    event.preventDefault()

    if (selectedFile) return

    setIsDragActive(false)
    const file = event.dataTransfer.files?.[0] || null

    if (!file) return

    if (!isValidXlsxFile(file)) {
      setFileTypeError('Only .xlsx files are allowed.')
      return
    }

    setFileTypeError('')

    previewUploadedFile(file)
  }

  const handleRemoveUploadedFile = () => {
    setSelectedFile(null)
    setFormData(initialFormData)
    setIsDragActive(false)
    setMissingMetadataFields([])
    setFileTypeError('')
    setPrefillValidationErrors(null)
    setIsFormDataReady(false)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const previewUploadedFile = async (file) => {
    setSelectedFile(file)
    setIsPreviewLoading(true)
    setMissingMetadataFields([])
    setFileTypeError('')
    setPrefillValidationErrors(null)
    setIsFormDataReady(false)

    try {
      const form = new FormData()
      form.append('file', file)

      const response = await fetch(`${API_BASE_URL}/api/company-metadata/preview`, {
        method: 'POST',
        body: form,
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || 'Failed to preview uploaded file')
      }

      const result = await response.json()
      const metadata = result?.metadata || {}
      const mappedFormData = mapMetadataToFormData(metadata)
      const missingFields = getMissingMetadataFields(mappedFormData)

      setMissingMetadataFields(missingFields)

      // Validate prefilled values against template
      const validation = await validateTemplateValues(
        mappedFormData.industry,
        mappedFormData.sector,
        mappedFormData.subsector,
      )

      let finalFormData = mappedFormData

      if (!validation.is_template_match) {
        // Clear invalid prefilled values so user must manually select from dropdowns
        finalFormData = { ...mappedFormData }
        if (validation.industry && !validation.industry.is_valid) {
          finalFormData.industry = ''
        }
        if (validation.sector && !validation.sector.is_valid) {
          finalFormData.sector = ''
        }
        if (validation.subsector && !validation.subsector.is_valid) {
          finalFormData.subsector = ''
        }
        setPrefillValidationErrors(validation)
      } else {
        setPrefillValidationErrors(null)
      }

      setFormData((previous) => ({
        ...previous,
        ...finalFormData,
      }))
      setIsFormDataReady(true)
    } catch (error) {
      console.error('Metadata preview failed', error)
      alert('Unable to fetch metadata preview: ' + (error.message || error))
      setIsFormDataReady(false)
    } finally {
      setIsPreviewLoading(false)
    }
  }

  // Define optional fields that should not block form submission
  const optionalFields = new Set(['ltmDate', 'censusDate', 'freeFormComments', 'dataType', 'date'])
  const isFormEnabled = Boolean(selectedFile) && isFormDataReady && !isPreviewLoading
  const remainingMissingFields = getRemainingMissingFields(missingMetadataFields, formData)

  const isFormComplete =
    Boolean(selectedFile) &&
    Object.entries(formData)
      .filter(([key]) => !optionalFields.has(key))
      .every(([, value]) => String(value).trim() !== '')

  const handleSubmit = () => {
    if (isSubmitting) return

    setMissingMetadataFields([])

    if (!selectedFile) return

    const metadataPayload = buildMetadataPayload(formData)

    if (!metadataPayload.project_name) {
      alert('Project Name is required before submission.')
      return
    }

    const form = new FormData()
    form.append('file', selectedFile)
    form.append('metadata', JSON.stringify(metadataPayload))

    setIsSubmitting(true)

    fetch(`${API_BASE_URL}/api/contribute/upload`, {
      method: 'POST',
      body: form,
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text()
          throw new Error(text || 'Upload failed')
        }
        return res.json()
      })
      .then((data) => {
        setShowSubmitSuccessPopup(true)

        // Fire KPI calculations in the background without blocking the submit UX.
        runSharedKpiCalculations().catch((error) => {
          console.error('KPI calculation trigger from submit failed', error)
        })

        // reset UI
        setSelectedFile(null)
        setFormData(initialFormData)
        setIsDragActive(false)
        setMissingMetadataFields([])
        setPrefillValidationErrors(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      })
      .catch((err) => {
        console.error('Upload error', err)
        alert('Upload failed: ' + (err.message || err))
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

   const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = TEMPLATE_FILE_PATH;
    link.download = TEMPLATE_FILE_NAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`${isDarkMode ? 'bg-gray-950' : 'bg-[#f4fbf6]'} min-h-screen relative overflow-hidden`}>
      {/* Atmospheric blobs */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-emerald-200/30 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-40 w-[440px] h-[440px] rounded-full bg-sky-200/25 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-teal-200/20 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-10">
        {/* Page header */}
        <div className="mb-8">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800 mb-3">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Data Contribution
          </span>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">Contribute Data</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Upload your standardized template and review extracted metadata before submitting.</p>
        </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="lg:w-1/2 bg-white/95 dark:bg-gray-900/95 border border-emerald-100 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur p-6 align-middle">
         <div className="min-w-[260px] w-full text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
               Download Template
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Please download the Standardization Template, complete all required fields in accordance with the field instructions, and upload the finalized file using the 'Upload File' tab below.
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-emerald-700 text-white hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-300/40 transition-all"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Template
            </button>
            </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 mt-6">
            Upload File <span className="text-red-500">*</span>
          </h3>
          <label
            htmlFor="contribute-file-upload"
            onDragOver={(event) => {
              if (selectedFile) return
              event.preventDefault()
              setIsDragActive(true)
            }}
            onDragLeave={() => {
              if (selectedFile) return
              setIsDragActive(false)
            }}
            onDrop={handleDrop}
            className={`
              block w-full border-2 border-dashed rounded-xl p-8 text-center transition-all
              ${isDragActive
                ? 'border-emerald-500 bg-emerald-50 dark:bg-gray-900'
                : 'border-emerald-200 dark:border-gray-700 hover:border-emerald-400 hover:bg-emerald-50/40'}
              ${selectedFile ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}
            `}
          >
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              Drag and drop your file here
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">or</p>
            <span className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-emerald-700 text-white hover:bg-emerald-800 transition-all">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Upload from System
            </span>
            <input
              ref={fileInputRef}
              id="contribute-file-upload"
              type="file"
              accept=".xlsx"
              className="hidden"
              disabled={Boolean(selectedFile)}
              onChange={handleFileChange}
            />
          </label>
          {fileTypeError && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
              {fileTypeError}
            </p>
          )}
          {selectedFile && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Selected file: <span className="font-medium">{selectedFile.name}</span>
              </p>
              <button
                type="button"
                onClick={handleRemoveUploadedFile}
                className="text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                Remove file
              </button>
            </div>
          )}
          {isPreviewLoading && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Extracting metadata from uploaded file...
            </p>
          )}
          {remainingMissingFields.length > 0 && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
              Missing fields: {remainingMissingFields.join(', ')}
            </div>
          )}
        </div>

        <div className="lg:w-1/2 bg-white/95 dark:bg-gray-900/95 border border-sky-100 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur p-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Review Details
          </h3>
          {dropdownError && (
            <p className="mb-3 text-sm text-red-600 dark:text-red-400">
              {dropdownError}
            </p>
          )}
          {prefillValidationErrors?.is_template_match === false && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
              <p className="font-semibold mb-1">Template Validation Warning</p>
              <p>Some extracted values don't match the template. Please reselect from the dropdowns.</p>
              {prefillValidationErrors.industry && !prefillValidationErrors.industry.is_valid && (
                <p className="text-xs mt-1">• Industry "{prefillValidationErrors.industry.value}" is invalid</p>
              )}
              {prefillValidationErrors.sector && !prefillValidationErrors.sector.is_valid && (
                <p className="text-xs">• Sector "{prefillValidationErrors.sector.value}" is invalid</p>
              )}
              {prefillValidationErrors.subsector && !prefillValidationErrors.subsector.is_valid && (
                <p className="text-xs">• Subsector "{prefillValidationErrors.subsector.value}" is invalid</p>
              )}
            </div>
          )}
          {!isFormEnabled && (
            <p className="mb-4 text-sm text-amber-700 dark:text-amber-300">
              Upload a file to enable the form fields.
            </p>
          )}

          <fieldset disabled={!isFormEnabled} className={!isFormEnabled ? 'opacity-60' : ''}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Project Name (Target) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 border border-emerald-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 hover:border-emerald-400 transition-colors"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Account Aligned (PE Firm) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="accountAligned"
                value={formData.accountAligned}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 border border-sky-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 hover:border-sky-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                LTM Date 
              </label>
              <input
                type="date"
                name="ltmDate"
                value={formData.ltmDate}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400 hover:border-gray-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Census Date 
              </label>
              <input
                type="date"
                name="censusDate"
                value={formData.censusDate}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400 hover:border-gray-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Industry <span className="text-red-500">*</span>
              </label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 border border-emerald-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 hover:border-emerald-400 transition-colors"
              >
                <option value="">-- Select Industry --</option>
                {formData.industry && !industryOptions.includes(formData.industry) && (
                  <option value={formData.industry}>{formData.industry}</option>
                )}
                {industryOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Sector <span className="text-red-500">*</span>
              </label>
              <select
                name="sector"
                value={formData.sector}
                onChange={handleInputChange}
                disabled={!formData.industry}
                className="w-full h-10 px-3 py-2 border border-sky-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 hover:border-sky-400 transition-colors"
              >
                <option value="">-- Select Sector --</option>
                {formData.sector && !sectorOptions.includes(formData.sector) && (
                  <option value={formData.sector}>{formData.sector}</option>
                )}
                {sectorOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Subsector <span className="text-red-500">*</span>
              </label>
              <select
                name="subsector"
                value={formData.subsector}
                onChange={handleInputChange}
                disabled={!formData.sector}
                className="w-full h-10 px-3 py-2 border border-indigo-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-indigo-400 transition-colors"
              >
                <option value="">-- Select Subsector --</option>
                {formData.subsector && !subsectorOptions.includes(formData.subsector) && (
                  <option value={formData.subsector}>{formData.subsector}</option>
                )}
                {subsectorOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

             <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Geography <span className="text-red-500">*</span>
              </label>
              <select
                name="geography"
                value={formData.geography}
                onChange={handleInputChange}
                disabled={!formData.sector}
                className="w-full h-10 px-3 py-2 border border-amber-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 hover:border-amber-400 transition-colors"
              >
                <option value="">-- Select Geography --</option>
                {geography.map((item) => (
                  <option key={item.id} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

             <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Ownership Type <span className="text-red-500">*</span>
              </label>
              <select
                name="ownershipType"
                value={formData.ownershipType}
                onChange={handleInputChange}
                disabled={!formData.sector}
                className="w-full h-10 px-3 py-2 border border-teal-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 hover:border-teal-400 transition-colors"
              >
                <option value="">-- Select Ownership Type --</option>
                {ownerShipType.map((item) => (
                  <option key={item.id} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Company Revenue (million USD) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="companyRevenue"
                value={
                  String(formData.companyRevenue ?? "").trim() === ""
                    ? ""
                    : Number.isFinite(Number(formData.companyRevenue))
                      ? String(Number(formData.companyRevenue).toFixed(1))
                      : String(formData.companyRevenue)
                }
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 border border-emerald-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 hover:border-emerald-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                EBITDA (million USD per P&L)<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="ebitda"
                value={
                  String(formData.ebitda ?? "").trim() === ""
                    ? ""
                    : Number.isFinite(Number(formData.ebitda))
                      ? String(Number(formData.ebitda).toFixed(1))
                      : String(formData.ebitda)
                }
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 border border-sky-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 hover:border-sky-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Number of Employees <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="numberOfEmployees"
                value={formData.numberOfEmployees}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 border border-indigo-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-indigo-400 transition-colors"
              />
            </div>
            {/* <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Free Form Comments
              </label>
              <input
                type="text"
                name="freeFormComments"
                value={formData.freeFormComments}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div> */}
          </div>
          </fieldset>
        </div>
      </div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isFormComplete || isSubmitting}
            className={`inline-flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-sm bg-emerald-700 text-white hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-300/40 transition-all ${(!isFormComplete || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Submit
          </button>
        </div>

        {isSubmitting && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <div className="w-full max-w-sm rounded-2xl border border-emerald-100 bg-white dark:bg-gray-900 shadow-[0_20px_60px_rgba(16,24,40,0.15)] p-6 text-center">
              <div
                className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-300 dark:border-gray-700 border-t-green-600"
                role="status"
                aria-label="Submitting"
              ></div>
            </div>
          </div>
        )}

        {showSubmitSuccessPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <div className="w-full max-w-sm rounded-2xl border border-emerald-100 bg-white dark:bg-gray-900 shadow-[0_20px_60px_rgba(16,24,40,0.15)] p-6 text-center">
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                Submitted successfully
              </h4>
              <button
                type="button"
                onClick={() => setShowSubmitSuccessPopup(false)}
                className="mt-4 px-6 py-2.5 rounded-xl font-semibold text-sm bg-emerald-700 text-white hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-300/40 transition-all"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    
    </div>
  )
}

export default ContributeDataPage